import { getOrderContactListDataByOrderId } from '../orderContacts/mockData'
import { getOrderTeamMemberListDataByOrderId } from '../orderTeamMembers/mockData'
import { getOrderDocumentListDataByOrderId } from '../orderDocuments/mockData'
import { getOrderNoteListDataByOrderId } from '../orderNotes/mockData'
import { getOrderMasterServiceListDataByOrderId } from '../orderMasterServices/mockData'
import { getOrderServiceOrderListDataByOrderId } from '../orderServiceOrders/mockData'
import { getOrderLastSelectedStateDataByOrderId } from '../orderLastSelectedStates/mockData'
import { getTaskListDataByOrderId } from '../tasks/mockData'
import { getCallListDataByOrderId } from '../calls/mockData'
import { getEmailListDataByOrderId } from '../emails/mockData'
import { getMeetingListDataByOrderId } from '../meetings/mockData'
import { getCompanyListDataByAccountOwnerId } from '../companies/mockData'
import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import { _storeEntityDataListToLocalForage } from '../../modules/mockDataStorageManager'
import { getOrderOverviewContractListData } from '../contracts/mockData'
import {
  OrderStatusIds,
  getOrderStatusById,
} from '~/services/selectLists/orderStatii'
import {
  getOrderTypeById,
  OrderTypeIds,
} from '~/services/selectLists/orderTypes'
import type { ICompany } from '~/types/company/models'
import type { IOrderOverview, IOrder } from '~/types/order/models'
import type {
  TEntityId,
  ISelectItem,
  IEntitySelectItemWithOptionalAvatar,
} from '~/types/models'
import {
  OrderMSServicesStatusIds,
  getOrderMSServiceStatusById,
} from '~/services/selectLists/orderMSServicesStatii'
import {
  ContractStatusIds,
  getContractStatusById,
} from '~/services/selectLists/contractStatii'

const mn = '[api/msw/api/modules/orders/mockdData.ts]'
const orderListData = undefined as unknown as IOrder[]

export const getOrderListData = getListDataFunctionFactory<IOrder[]>({
  listData: orderListData,
  storageKey: 'orders',
  getStartingListData: getStartingOrderListData,
  forOfFunction: async (o) => {
    // const momsArray = await getOrderMasterServiceListDataByOrderId(
    //   o.id
    // )
    // console.debug('EXECUTING FOR EACH LOOP', o.id, o)
    const momsArray = await getOrderMasterServiceListDataByOrderId(o.id)
    // console.debug('EXECUTING AFTER AWAIT', o.id, momsArray)
    o.numMasterServices = momsArray.length
  },
})

// Distinct suppliers across all of the order's master services' MS services (gated on
// supplier + doInclude). Mirrors getSupplierListDataByOpportunityId /
// getSupplierListDataByOrderMasterServiceId, aggregated to the order level.
export async function getSupplierListDataByOrderId(
  id: string,
  onlyWithReadyForContractMSServices: boolean = false
): Promise<IEntitySelectItemWithOptionalAvatar[]> {
  const masterServices = await getOrderMasterServiceListDataByOrderId(id)
  return masterServices.reduce(
    (unique: IEntitySelectItemWithOptionalAvatar[], oms) => {
      for (const omss of oms.orderMSServices ?? []) {
        if (omss.supplier && omss.doInclude) {
          if (
            !onlyWithReadyForContractMSServices ||
            omss.status.id === OrderMSServicesStatusIds.READY_FOR_CONTRACT
          ) {
            if (!unique.some((s) => s.id === omss.supplier?.id)) {
              unique.push(omss.supplier)
            }
          }
        }
      }
      return unique
    },
    []
  )
}

export function _getMODataById(
  id: TEntityId,
  list: IOrder[]
): IOrder | undefined {
  if (!list) {
    throw new Error(`_getMODataById called with undefined/null list`)
  }
  return list.find((cct: IOrder) => cct.id === id)
}

export function _getMOSelectItemDataById(
  id: TEntityId,
  list: IOrder[]
): ISelectItem {
  if (!list) {
    throw new Error(
      `export async function _getMOSelectItemDataById` +
        ` called with undefined/null list`
    )
  }
  const order = _getMODataById(id, list)
  if (!order) {
    throw new Error('Order mock data is falsey')
  }
  return { id: order.id, label: order.descr }
}

export async function getOrderDataById(
  id: string
): Promise<IOrder | undefined> {
  const list = await getOrderListData()
  return _getMODataById(id, list)
}

export async function getOrderSelectItemDataForId(id: string) {
  const list = await getOrderListData()
  return _getMOSelectItemDataById(id, list)
}

export async function getOrderListDataByCompanyId(id: string) {
  const oListData = await getOrderListData()
  const coListData = oListData.filter((o: IOrder) => o.company.id === id)
  if (!coListData) {
    throw new Error('Company Orders mock data (coListData) is falsey')
  }
  return coListData
}

export async function getOrderListDataByCompanyAccountOwnerId(
  id: string
): Promise<IOrder[]> {
  const cListData = await getCompanyListDataByAccountOwnerId(id)
  const companyIdList = cListData.map((c) => c.id)
  console.debug('companyIdList:', companyIdList)
  const oListData = await getOrderListData()
  const oppList =
    oListData.filter((opp: IOrder) =>
      companyIdList.includes(opp.company.id as string)
    ) ?? []
  return oppList
}

export async function getOrderOverviewDataByOpportunityId(id: string) {
  const orderId = await getOrderIdByOpportunityId(id)
  if (!orderId) {
    return null // This may or may not be the right thing to do. JR.
  }
  const orderOverviewData = await getOrderOverviewData(orderId)
  if (!orderOverviewData) {
    throw new Error(
      `OrderOverviewData data for order id '${id}' not found. This should not happen.`
    )
  }
  return orderOverviewData
}

export async function getOrderIdByOpportunityId(id: string) {
  const allOrders = await getOrderListData()
  if (!allOrders) {
    throw new Error(`Order list data for ${id} not found`)
  }
  const orderForOpportunityId = allOrders.filter(
    (order) => order.opportunity?.id === id
  )
  if (!orderForOpportunityId || orderForOpportunityId.length < 1) {
    console.debug(
      `Couldn't find any orders with opportunity id '${id}'. Returning undefined.`
    )
    return undefined
  }
  if (orderForOpportunityId.length > 1) {
    throw new Error(`Found more than one order with opportunity id '${id}'`)
  }
  console.debug(
    `Returning order id '${orderForOpportunityId[0].id}' for opportunity id '${id}.'`
  )
  return orderForOpportunityId[0].id
}

export async function getOrderOverviewData(
  id: string
): Promise<IOrderOverview | undefined> {
  const theOrderData = await getOrderDataById(id)
  if (!theOrderData) {
    console.debug(
      `Could not retrieve orderOverviewData for order id ${id}. Returning undefined.`
    )
    return undefined
  }
  if (!theOrderData.company) {
    throw new Error(`Order data for ${id} does not contain a company object`)
  }
  if (!theOrderData.company.id) {
    throw new Error(
      `Order data for ${id} contains a company object, but that object has no id value.`
    )
  }
  const theOverviewData = {
    order: theOrderData,
    orderTeamMembers: await getOrderTeamMemberListDataByOrderId(id),
    orderContacts: await getOrderContactListDataByOrderId(id),
    orderDocuments: await getOrderDocumentListDataByOrderId(id),
    orderNotes: await getOrderNoteListDataByOrderId(id),
    orderMasterServices: await getOrderMasterServiceListDataByOrderId(id),
    contracts: await getOrderOverviewContractListData({
      companyId: theOrderData?.company?.id,
      orderId: id,
    }),
    orderServiceOrders: await getOrderServiceOrderListDataByOrderId(id),
    lastSelectedState: await getOrderLastSelectedStateDataByOrderId(id),
    tasks: await getTaskListDataByOrderId(id),
    calls: await getCallListDataByOrderId(id),
    emails: await getEmailListDataByOrderId(id),
    meetings: await getMeetingListDataByOrderId(id),
  }
  return theOverviewData
}

export async function getOrderDataByOpportunityId(
  id: string
): Promise<IOrder | null> {
  const list = await getOrderListData()
  if (!list) {
    throw new Error(`getOrderListData returned undefined/null`)
  }
  const order = list.find((o: IOrder) => o.opportunity?.id === id)
  if (!order) {
    console.debug(`Can't find order for id '${id}'. Returning null.`)
  }
  return order || null
}

export async function getOrderListDataByAssignedToId(
  id: string
): Promise<IOrder[]> {
  const list = await getOrderListData()
  if (!list) {
    throw new Error(`getOrderListData returned undefined/null`)
  }
  const teamMemberOrderList =
    list.filter((o: IOrder) => o.assignedTo?.id === id) ?? []
  return teamMemberOrderList
}

export async function getUnassignedOrderListData(): Promise<IOrder[]> {
  const list = await getOrderListData()
  if (!list) {
    throw new Error(`getOrderListData returned undefined/null`)
  }
  const unassignedOrderList = list.filter((o: IOrder) => !o.assignedTo) ?? []
  return unassignedOrderList
}

export async function updateCompanyInOrders({
  targetItem: company,
}: {
  targetItem: ICompany
}) {
  const fn = `${mn} updateCompanyInOrders |`
  const list: IOrder[] = await getOrderListData()
  if (!list) {
    throw new Error(`getOrderListData returned undefined/null`)
  }
  for (const order of list) {
    if (order.company.id === company.id) {
      console.debug(
        `${fn} updating companyInOrder from ${order.company.label} to ${company.label}`
      )
      order.company = { id: company.id, label: company.name }
    }
  }
  await _storeEntityDataListToLocalForage({
    entityKeyBase: 'orders',
    listData: list,
    callingFn: fn,
  })
}

export async function getStartingOrderListData(): Promise<IOrder[]> {
  return [
    {
      id: 'XXORD0001',
      hrid: 'ORD0001',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'DIA - Target & Big Mac',
      suppliers: [
        {
          id: 'XXS0002',
          label: 'Cogent',
        },
      ],
      // opportunity: {
      //   id: 'XXO0003',
      //   label: 'DIA - All Locations',
      // },
      term: 24,
      modifiedAt: '2024-06-30T18:39:00.000Z',
      lastUpdatedDate: '2024-06-30',
      createdAt: '2024-06-30',
      status: getOrderStatusById(OrderStatusIds.READY_FOR_CONTRACTS)!,
      orderType: [getOrderTypeById(OrderTypeIds.NEW)!],
      product: {
        id: 'XXPR0001',
        label: 'DIA',
      },
      location: {
        id: 'XXL0000',
        label: 'N/A',
      },
      defaultProject: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Conversion',
      },
      assignedTo: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      numMasterServices: 1,
      numOrderMSServices: 10,
      numContracts: 1,
      numOrderServiceOrders: 0,
      momssStatusCounts: [
        {
          status: getOrderMSServiceStatusById(
            OrderMSServicesStatusIds.READY_FOR_CONTRACT
          ),
          count: 10,
        },
      ],
      contractStatusCounts: [
        {
          status: getContractStatusById(ContractStatusIds.NO_SERVICES),
          count: 1,
        },
      ],
      orderServiceOrderStatusCounts: [],
      version: '1',
      canBeDeleted: true,
    },
    /**
     * XXORD0002 -- the order that signing XXCON0006 produces.
     *
     * ⚠ IT IS A SEPARATE ORDER FROM XXORD0001 ON PURPOSE. XXORD0001 was never
     * created from an opportunity: it carries no `opportunity`, its master
     * service carries no `opportunityMasterService`, none of its ten msServices
     * carries an `opportunityMSService`, its descr is 'DIA - Target & Big Mac'
     * against XXO0003's own name, and its master service's `origin` is
     * 'Existing' where XXOMS0005's is 'New'. Every one of those is a field the
     * signing routine copies or writes without exception, so XXORD0001 is a
     * stand-alone order -- built directly in the Order silo -- and attaching
     * XXO0003's services to it would invent a history it does not have.
     *
     * `buildOrderOverviewForPostFromOpportunityOverview` therefore finds no
     * preexisting order for XXO0003 (`getOrderOverviewForOpportunity` returns
     * undefined) and `_buildOrderFromOpportunity` builds this one, taking
     * `descr` and `orderType` straight from the opportunity -- which is why the
     * name matches XXO0003 exactly rather than naming the one location its
     * services are at.
     *
     * Only XXCON0006's services are here. XXCON0002 is still DRAFT, so its
     * Closed/Won rows have not been cloned to any order -- that is the whole
     * point of leaving it unsigned.
     */
    {
      id: 'XXORD0002',
      hrid: 'ORD0002',
      company: {
        id: 'XXC0001',
        label: 'Metropolis Bank',
      },
      descr: 'DIA - Boston, Lake & Waltham',
      suppliers: [
        {
          id: 'XXS0002',
          label: 'Cogent',
        },
      ],
      opportunity: {
        id: 'XXO0003',
        label: 'DIA - Boston, Lake & Waltham',
      },
      term: 36,
      modifiedAt: '2024-03-15T15:00:00.000Z',
      lastUpdatedDate: '2024-03-15',
      createdAt: '2024-03-15',
      status: getOrderStatusById(OrderStatusIds.PREORDER)!,
      orderType: [getOrderTypeById(OrderTypeIds.NEW)!],
      product: {
        id: 'XXPR0001',
        label: 'DIA',
      },
      location: {
        id: 'XXL0009',
        label: 'Waltham',
      },
      defaultProject: {
        id: 'XXP0002',
        label: 'E-LAN to DIA Migration',
      },
      assignedTo: {
        id: 'XXTM0001',
        label: 'Meryl Streep',
      },
      numMasterServices: 1,
      numOrderMSServices: 5,
      numContracts: 1,
      numOrderServiceOrders: 0,
      momssStatusCounts: [
        {
          status: getOrderMSServiceStatusById(
            OrderMSServicesStatusIds.PREORDER_LOCKED
          ),
          count: 5,
        },
      ],
      contractStatusCounts: [
        {
          status: getContractStatusById(ContractStatusIds.SIGNED),
          count: 1,
        },
      ],
      orderServiceOrderStatusCounts: [],
      version: '1',
      canBeDeleted: true,
    },
  ]
}
